import { Button, Checkbox, FormControlLabel, CircularProgress } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import {
  postWithQueryApiServices,
  putWithQueryApiServices,
} from "../../../apis/api";
import { apiRoutes } from "../../../apis/apiPath";
import { subscriptionFormInitVal } from "../../../constants";
import { StyledTypography } from "../../../styles/commonStyles";
import { errorToast, successToast } from "../../../utils/helper";
import { FullFeaturedCrudGrid } from "../../shared/CommonTable";
import { CustomTextField } from "../../shared/CustomTextField";

const columns = [
  { field: "item", headerName: "Item / Service", flex: 1, editable: true },
  {
    field: "quantity",
    headerName: "Quantity",
    align: "left",
    headerAlign: "left",
    editable: true,
    flex: 0.5,
    type: "number",
  },
  {
    field: "price",
    headerName: "Price",
    align: "left",
    headerAlign: "left",
    editable: true,
    flex: 0.5,
    type: "number",
  },
  {
    field: "recurring",
    headerName: "Recurring",
    align: "left",
    headerAlign: "left",
    editable: true,
    flex: 0.5,
    type: "boolean",
    required: false
  },
];
export const SubscriptionForm = ({
  categoryListRefetch,
  companyListRefetch,
  companyForm,
  setCompanyForm,
  categoryData,
  setCategoryData,
  addNewCategory,
  setAddNewCategory,
  setSelectedCategories,
  permission,
}) => {
  const [categoryValues, setCategoryValues] = useState({
    ...subscriptionFormInitVal,
    shippingcost: 0
  });
  const [criteriaValues, setCriteriaValues] = useState([]);
  const [showShippingCost, setShowShippingCost] = useState(false);
  
  const { currentCompany, tenantString } = useOutletContext();

  const formHandleChange = (name, value) => {
    setCategoryValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Define required fields for the custom form
  const requiredFields = [
    { name: "name", label: "Name" },
    { name: "title", label: "Title" },
    { name: "description", label: "Description" },
    { name: "benfits", label: "Benefits" },
    { name: "tax", label: "Tax %" },
    ...(showShippingCost ? [{ name: "shippingcost", label: "Shipping Cost" }] : [])
  ];

  const sumbitCompanyForm = () => {
    const missingFields = requiredFields
      .filter(field => categoryValues[field.name] === undefined || categoryValues[field.name] === "")
      .map(field => field.label);

    if (missingFields.length > 0) {
      return errorToast(`Please fill the following fields: ${missingFields.join(", ")}`);
    }
    if (criteriaValues.length < 1) {
      return errorToast(`Please add subscription item`);
    }

    if (categoryData?.subscriptionID) {
      updateCategory.mutate();
    } else {
      createCategory.mutate();
    }
  };

  const createCategory = useMutation({
    mutationKey: ["createSubscriptionform"],
    mutationFn: () =>
      postWithQueryApiServices(
        apiRoutes.GET_SUBSCRIPTION_LIST,
        `selectedCompany=${currentCompany}${tenantString}`,
        { ...categoryValues, subscriptionitem: criteriaValues }
      ),
    onSuccess: ({ data }) => {
      setAddNewCategory(false);
      categoryListRefetch();
      successToast("Subscription added Successfully");
      setCategoryValues(subscriptionFormInitVal);
      setSelectedCategories([]);
      setCategoryData({});
    },
  });

  const updateCategory = useMutation({
    mutationKey: ["updateSubscriptionform"],
    mutationFn: () =>
      putWithQueryApiServices(
        apiRoutes.GET_SUBSCRIPTION_LIST,
        `selectedCompany=${currentCompany}${tenantString}`,
        { ...categoryValues, subscriptionitem: criteriaValues }
      ),
    onSuccess: () => {
      categoryListRefetch();
      setAddNewCategory(false);
      successToast("Subscription Updated Successfully");
      setCategoryValues(subscriptionFormInitVal);
      setCategoryData({});
      setSelectedCategories([]);
    },
  });

  const isSaving = createCategory.isPending || updateCategory.isPending;

  const rowUpdateFun = (newRow) => {
    const requiredColumns = columns.filter(col => col.required !== false);
    const columnNames = requiredColumns.map((obj) => obj.field);
    const missingFields = columnNames.filter((key) => newRow[key] === undefined || newRow[key] === "");
    if (missingFields.length > 0) {
      const missingFieldsMsg = missingFields
        .map((key) => {
          const col = columns.find((c) => c.field === key);
          return col && col.headerName ? col.headerName : key;
        })
        .join(", ");
      return errorToast(`Please fill the following fields: ${missingFieldsMsg}`);
    }else{
      if (newRow.isNew) {
        setCriteriaValues((prev) => [
          ...prev,
          { ...newRow, isNew: false, subscriptionitemID: newRow.id },
        ]);
      } else {
        const updatedRow = [...criteriaValues].map((item) => {
          if (item.subscriptionitemID === newRow.subscriptionitemID) {
            return { ...newRow };
          } else {
            return item;
          }
        });
        setCriteriaValues(updatedRow);
      }
      return { ...newRow, isNew: false };
    }
  };

  useEffect(() => {
    if (categoryData?.subscriptionID) {
      setCategoryValues({
        ...categoryData,
        duedate: new Date(categoryData.duedate),
        subscriptiondate: new Date(categoryData.subscriptiondate),
        shippingcost: categoryData.shippingcost !== undefined && categoryData.shippingcost !== null ? categoryData.shippingcost : 0
      });
      setShowShippingCost(!!categoryData?.shippingcost && categoryData.shippingcost !== "0" && categoryData.shippingcost !== 0);

      if (categoryData?.subscriptionitem?.length > 0) {
        setCriteriaValues(JSON.parse(categoryData?.subscriptionitem));
      } else {
        setCriteriaValues([]);
      }
    } else {
      setCategoryValues({
        ...subscriptionFormInitVal,
        shippingcost: 0
      });
      setCriteriaValues([]);
      setShowShippingCost(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryData?.subscriptionID]);

  useEffect(() => {
    let amount = criteriaValues?.reduce((sum, obj) => {
      return sum + parseInt(obj.quantity) * parseInt(obj.price);
    }, 0);
    let recurring_amount = criteriaValues?.reduce((sum, obj) => {
      if (obj.recurring) {
        return sum + parseInt(obj.quantity) * parseInt(obj.price);
      } else {
        return sum;
      }
    }, 0);
    if (categoryValues?.tax) {
      amount = parseInt(amount * (1 + categoryValues?.tax / 100));
      recurring_amount = parseInt(
        recurring_amount * (1 + categoryValues?.tax / 100)
      );
    }
    if (categoryValues?.shippingcost) {
      amount = parseInt(amount) + parseInt(categoryValues?.shippingcost);
      if (recurring_amount) {
        recurring_amount =
          parseInt(recurring_amount) + parseInt(categoryValues?.shippingcost);
      }
    }
    formHandleChange("amount", amount);
    formHandleChange("recurring_amount", recurring_amount);
  }, [criteriaValues, categoryValues?.tax, categoryValues?.shippingcost]);
  return (
    <div
      style={{
        maxHeight: "80vh",
        height: "80vh",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          height: "92%",
          borderRadius: 10,
          // padding: "2px 40px",
          boxSizing: "border-box",
          position: "relative",
          pointerEvents: !addNewCategory ? "none" : "auto",
          backgroundColor: !addNewCategory ? "#f0f0f0" : "white",
        }}
      >
        <div
          style={{
            minHeight: "85%",
            maxHeight: "85%",
            overflowY: "auto",
            opacity: !addNewCategory ? "0.6" : "1",
            filter: !addNewCategory ? "blur(5px)" : "none",
            transition: "filter 0.3s ease",
            pointerEvents: !addNewCategory ? "none" : "auto",
            cursor: !addNewCategory ? "not-allowed" : "auto",
            borderRadius: 10,
          }}
        >
          <FullFeaturedCrudGrid
            hideFooterPagination={true}
            initialRows={criteriaValues}
            delFun={setCriteriaValues}
            columns={columns}
            disabled={!addNewCategory}
            label="subscriptionitem"
            rowUpdateFun={rowUpdateFun}
            handleSelection={() => {}}
            refetch={categoryListRefetch}
            permission={permission}
            addNewTrigger={addNewCategory}
            isLocalTable={true}
          ></FullFeaturedCrudGrid>
          <div
            style={{
              padding: "2px 40px",
            }}
          >
            <StyledTypography></StyledTypography>
            <CustomTextField
              label="Name"
              name="name"
              value={categoryValues?.name}
              onChange={(e) => formHandleChange(e.target.name, e.target.value)}
            />
            <StyledTypography></StyledTypography>
            <CustomTextField
              label="Title"
              name="title"
              value={categoryValues?.title}
              onChange={(e) => formHandleChange(e.target.name, e.target.value)}
            />
            <StyledTypography></StyledTypography>
            <CustomTextField
              label="Description"
              name="description"
              value={categoryValues?.description}
              onChange={(e) => formHandleChange(e.target.name, e.target.value)}
            />
            <StyledTypography></StyledTypography>
            <CustomTextField
              label="Benfits"
              name="benfits"
              value={categoryValues?.benfits}
              onChange={(e) => formHandleChange(e.target.name, e.target.value)}
            />
            <StyledTypography></StyledTypography>
            <CustomTextField
              fieldType="decimal"
              label="Tax %"
              name="tax"
              value={categoryValues?.tax}
              onChange={(e) => formHandleChange(e.target.name, e.target.value)}
            />
            <StyledTypography></StyledTypography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showShippingCost}
                  onChange={(_, checked) => {
                    setShowShippingCost(checked);
                    if (!checked) {
                      formHandleChange("shippingcost", 0);
                    }
                  }}
                  name="showShippingCost"
                  color="primary"
                  disabled={!addNewCategory}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      e.stopPropagation();
                      const newChecked = !showShippingCost;
                      setShowShippingCost(newChecked);
                      if (!newChecked) {
                        formHandleChange("shippingcost", 0);
                      }
                    }
                  }}
                  tabIndex={0}
                  inputProps={{ tabIndex: 0 }}
                />
              }
              label="Add Shipping Cost"
              style={{ marginBottom: 0 }}
            />
            {showShippingCost && (
              <CustomTextField
                defaultValue={0.0}
                label="Shiping Cost"
                name="shippingcost"
                fieldType="decimal"
                value={categoryValues?.shippingcost}
                onChange={(e) => formHandleChange(e.target.name, e.target.value)}
              />
            )}
            <div
              style={{
                justifyContent: "space-between",
                display: "flex",
                margin: "15px 0",
                fontWeight: 600,
              }}
            >
              Total : <span>{categoryValues?.amount}</span>
            </div>
          </div>
        </div>
        <div
          style={{
            display: "flex",
            paddingRight: "40px",
            justifyContent: "flex-end",
            opacity: !addNewCategory ? "0.6" : "1",
            filter: !addNewCategory ? "blur(5px)" : "none",
            transition: "filter 0.3s ease",
            pointerEvents: !addNewCategory ? "none" : "auto",
            cursor: !addNewCategory ? "not-allowed" : "auto",
          }}
          >
          {permission && (
            <Button
              variant="contained"
              style={{ marginRight: 20 }}
              onClick={sumbitCompanyForm}
              disabled={isSaving}
              startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {isSaving ? "Saving..." : (categoryData?.subscriptionID ? "Update" : "Save")}
            </Button>
          )}

          <Button
            variant="outlined"
            onClick={() => {
              setCategoryValues(subscriptionFormInitVal);
              setCriteriaValues([]);
              setCategoryData({});
              setAddNewCategory(false);
              setSelectedCategories([]);
              setCompanyForm([]);
            }}
            disabled={isSaving}
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};
